import type { StructureSummary } from "@/admin/types/hosts";

/**
 * Turn a stored attribute value into readable copy: pass through plain strings,
 * humanise snake_case codes (e.g. `gazebo_pavilion_pergola` ->
 * "Gazebo Pavilion Pergola"), and drop empties.
 */
export function humaniseDetailValue(value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value !== "string") return String(value);
  if (!value.includes("_")) return value;
  return value
    .replace(/_/g, " ")
    .replace(/\b\p{L}/gu, (char) => char.toUpperCase());
}

export function isOtherStructureValue(value: unknown): boolean {
  return (
    String(value ?? "")
      .trim()
      .toLowerCase() === "other"
  );
}

export function titleCaseStructureLabel(value: unknown): string {
  const label = String(value ?? "").trim();
  if (!label) return "";
  return label.toLowerCase().replace(/\b\p{L}/gu, (char) => char.toUpperCase());
}

/**
 * Title + grey sub-label for a structure card on the registration record.
 *
 * WPS-183: an "Other" structure must describe ITSELF. The property-level
 * default type passed in as `fallbackType` (e.g. "Residential (Single Family
 * Detached)") belongs to the primary residential structure only - it must
 * never leak onto a gazebo/garage/etc. The `t` param is optional so the helper
 * can be unit-tested without an i18n provider; callers in the app pass it.
 */
export function getStructureDisplay(
  structure: StructureSummary | null,
  fallbackType?: string,
  t?: (key: string, defaultValue: string) => string,
) {
  const tr = (key: string, defaultValue: string) =>
    t ? t(key, defaultValue) : defaultValue;
  const values = structure?.attribute_values ?? {};
  const isOtherStructure = isOtherStructureValue(
    values.structure_type || fallbackType,
  );
  const otherStructureType = isOtherStructure
    ? titleCaseStructureLabel(humaniseDetailValue(values.other_structure_type))
    : "";
  const structureName =
    humaniseDetailValue(values.structure_name) ||
    humaniseDetailValue(values.name);
  const typeLabel =
    otherStructureType ||
    structure?.label ||
    humaniseDetailValue(values.structure_type) ||
    fallbackType;
  const name =
    structureName ||
    otherStructureType ||
    structure?.label ||
    tr("registrations.mainStructure", "Main structure");
  // Sub-label for an "Other" structure must describe the structure itself,
  // never the property-level default type passed in as `fallbackType` (e.g.
  // "Residential (Single Family Detached)") - that default belongs to the
  // primary residential structure, not to a gazebo/garage/etc. The title
  // already shows the specific detail ("Gazebo Pavilion Pergola"), so the
  // sub-label states the "Other" category and never echoes the title back.
  const otherMeta =
    otherStructureType &&
    otherStructureType.toLowerCase() !== name.toLowerCase()
      ? otherStructureType
      : tr("registrations.otherStructureType", "Other type of structure");
  const defaultMeta =
    (structureName ? typeLabel : null) ||
    fallbackType ||
    humaniseDetailValue(values.structure_type) ||
    tr("registrations.registeredStructure", "Registered structure");
  return {
    name,
    meta: isOtherStructure ? otherMeta : defaultMeta,
    sqft:
      humaniseDetailValue(values.square_feet) ||
      humaniseDetailValue(values.size) ||
      humaniseDetailValue(values.roof_size),
    built:
      humaniseDetailValue(values.year_built) ||
      humaniseDetailValue(values.built),
  };
}
